import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
} from "@mui/material";
import { toast } from "react-toastify";
import { PageContainer, PageHeader, PageTitle } from "../../components/PageLayout";
import { getFeedbacks } from "../../services/feedbacksService";
import { Feedback, FeedbackType, FEEDBACK_TYPES } from "../../types/feedback.types";
import ViewFeedbackDetails from "./components/ViewFeedbackDetails/ViewFeedbackDetails";

const ALL = "ALL";
const PAGE_SIZE = 10;

/** `type` é string livre no backend: registros legados podem trazer "bug", "rating" etc. */
export const labelType = (type?: string) =>
  FEEDBACK_TYPES.find((option) => option.value === type)?.label ?? type ?? "—";

const Feedbacks: React.FC = () => {
  const [selectedType, setSelectedType] = useState<FeedbackType | typeof ALL>(ALL);
  const [allFeedbacks, setAllFeedbacks] = useState<Feedback[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(PAGE_SIZE);

  useEffect(() => {
    let cancelled = false;

    const loadFeedbacks = async () => {
      setLoading(true);

      try {
        const data = await getFeedbacks();
        if (!cancelled) setAllFeedbacks(data);
      } catch {
        if (!cancelled) {
          toast.error("Erro ao carregar feedbacks.");
          setAllFeedbacks([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadFeedbacks();

    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * Melhoria/Erro/Sugestão é o campo `type`, que não tem endpoint de filtro no
   * backend — o recorte é feito aqui. Ver `feedbacksService.ts`.
   */
  const feedbacks = useMemo(() => {
    if (selectedType === ALL) return allFeedbacks;
    return allFeedbacks.filter((feedback) => feedback.type === selectedType);
  }, [allFeedbacks, selectedType]);

  const visibleFeedbacks = useMemo(
    () => feedbacks.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [feedbacks, page, rowsPerPage]
  );

  // Volta a primeira pagina quando o recorte muda, senao cai numa pagina vazia.
  useEffect(() => {
    setPage(0);
  }, [selectedType]);

  const renderFeedbacks = () => {
    if (loading && !allFeedbacks.length)
      return (
        <TableRow>
          <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
            <CircularProgress size={28} />
          </TableCell>
        </TableRow>
      );

    if (!feedbacks.length)
      return (
        <TableRow>
          <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
            {selectedType !== ALL
              ? "Nenhum feedback encontrado para a categoria selecionada"
              : "Sem feedbacks para exibir"}
          </TableCell>
        </TableRow>
      );

    return visibleFeedbacks.map((feedback) => {
      const description = feedback.description ?? "";

      return (
        <TableRow key={feedback.id} hover>
          <TableCell sx={{ fontWeight: "bold" }}>
            {feedback.subject ?? feedback.title ?? "—"}
          </TableCell>
          <TableCell sx={{ whiteSpace: "nowrap" }}>{labelType(feedback.type)}</TableCell>
          <TableCell>
            <Tooltip
              title={description}
              placement="top-start"
              slotProps={{ tooltip: { sx: { maxWidth: 480, whiteSpace: "pre-wrap" } } }}
            >
              <Box
                sx={{
                  width: 420,
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  cursor: description ? "help" : "default",
                }}
              >
                {description || "—"}
              </Box>
            </Tooltip>
          </TableCell>
          <TableCell align="right">
            <ViewFeedbackDetails feedback={feedback} />
          </TableCell>
        </TableRow>
      );
    });
  };

  return (
    <PageContainer style={{ height: "100%", boxSizing: "border-box", overflow: "hidden" }}>
      <PageHeader>
        <PageTitle>Feedbacks</PageTitle>
        <FormControl size="small" sx={{ minWidth: 180, backgroundColor: "#fff" }}>
          <InputLabel id="type-select-label">Categoria</InputLabel>
          <Select
            labelId="type-select-label"
            value={selectedType}
            label="Categoria"
            onChange={(e) => setSelectedType(e.target.value as FeedbackType | typeof ALL)}
          >
            <MenuItem value={ALL}>Todas</MenuItem>
            {FEEDBACK_TYPES.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </PageHeader>

      <Paper
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <TableContainer sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
          <Table stickyHeader sx={{ minWidth: 650 }} aria-label="feedbacks table">
            <TableHead>
              <TableRow>
                <TableCell sx={{ whiteSpace: "nowrap" }}>Título</TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>Categoria</TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>Descrição</TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }} align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>{renderFeedbacks()}</TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={feedbacks.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10, 50, 100]}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          labelRowsPerPage="Itens por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
          sx={{ borderTop: 1, borderColor: "divider", flexShrink: 0 }}
        />
      </Paper>
    </PageContainer>
  );
};

export default Feedbacks;
